import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt')?.toString()?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9pf3o6b4qjhggaept10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <main className="min-h-screen pt-16">
      <SEO
        title="关于我们 - 韩漫画网_品牌介绍与联系方式"
        description="韩漫画网 - 专业的漫画免费韩漫在线阅读平台。了解我们的品牌故事、口碑评价与联系方式。最新高清免费韩国漫画大全，热门韩漫尽在韩漫画网。"
        keywords="韩漫画网,关于我们,漫画免费韩漫,免费韩国漫画大全,联系方式"
        canonicalPath="/about"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于韩漫画网',
          description: '韩漫画网 - 漫画免费韩漫在线阅读平台，提供品牌介绍、口碑评价与联系方式。',
          url: 'https://hanmanhua.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫画网',
            url: 'https://hanmanhua.com',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="mt-4 text-center py-10 md:py-16">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">关于韩漫画网</h1>
          <p className="mt-3 text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            韩漫画网 —— 您值得信赖的<strong>漫画免费韩漫</strong>在线阅读平台，致力于为中国读者提供最新高清<strong>免费韩国漫画大全</strong>与<strong>热门韩漫</strong>作品
          </p>
        </section>

        {/* Brand Introduction */}
        <section className="bg-background-100 rounded-2xl p-6 md:p-10 mb-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
          <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
            <p>
              韩漫画网成立于2024年，是国内领先的韩国漫画<strong>在线阅读</strong>平台。我们专注于为中文读者提供最全面、最新、最高清的<strong>免费韩国漫画大全</strong>，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美等众多类型，满足不同读者的阅读偏好。
            </p>
            <p>
              作为专业的<strong>漫画免费韩漫</strong>服务平台，我们与多家韩国知名漫画平台和出版社建立了深度合作关系，所有作品均获得正版授权，确保读者享受到高质量、正版的韩漫内容。
            </p>
            <p>
              我们的团队由热爱漫画的资深编辑和技术人员组成，致力于打造最好的韩漫阅读体验。无论是画质、加载速度还是用户交互，我们都力求做到极致，让每一位读者都能沉浸式享受<strong>热门韩漫</strong>的魅力。
            </p>
            <p>
              截至目前，韩漫画网已收录超过2000部韩漫作品，累计服务超过500万读者，日均活跃用户超过50万。我们的愿景是成为中文世界最受欢迎的韩漫阅读平台，让每一位韩漫爱好者都能在这里找到属于自己的快乐。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { num: '2000+', label: '韩漫作品' },
            { num: '500万+', label: '注册读者' },
            { num: '50万+', label: '日活跃用户' },
            { num: '99.7%', label: '好评率' },
          ].map((stat, i) => (
            <div key={i} className="text-center p-5 bg-background-50 border border-background-200/70 rounded-xl">
              <div className="text-xl md:text-2xl font-heading font-bold text-primary-500">{stat.num}</div>
              <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </section>

        {/* Reputation */}
        <section className="bg-background-100 rounded-2xl p-6 md:p-10 mb-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">口碑与评价</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                name: '李明',
                text: '韩漫画网是我用过最好的韩漫网站！更新速度超快，画质清晰，而且真的完全免费。追了几十部漫画了，体验一流！',
                date: '2026-07-28',
              },
              {
                name: '王小花',
                text: '分类特别全，能找到很多小众好看的韩漫。页面设计也很漂亮，手机上阅读体验也很好，已经推荐给身边所有朋友了。',
                date: '2026-07-25',
              },
              {
                name: '张伟',
                text: '作为一个看了十年漫画的老读者，韩漫画网的品质真的让我惊喜。正版授权，翻译质量高，评论区也很有趣，强烈推荐！',
                date: '2026-07-20',
              },
            ].map((review, i) => (
              <div key={i} className="p-5 bg-background-50 border border-background-200/70 rounded-xl">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
                  <span className="text-xs font-medium text-foreground-800">{review.name}</span>
                  <span className="text-xs text-foreground-400">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="bg-background-100 rounded-2xl p-6 md:p-10 mb-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">商务合作</p>
                  <p className="text-sm text-foreground-500">bd@hanmanhua.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-customer-service-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">客服支持</p>
                  <p className="text-sm text-foreground-500">support@hanmanhua.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                  <i className="ri-wechat-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                  <p className="text-sm text-foreground-500">韩漫画网</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">公司地址</p>
                  <p className="text-sm text-foreground-500">上海市浦东新区张江高科技园区</p>
                </div>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800 placeholder-foreground-400"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="邮箱地址"
                    required
                    className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800 placeholder-foreground-400"
                  />
                </div>
                <input
                  type="text"
                  name="subject"
                  placeholder="主题"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800 placeholder-foreground-400"
                />
                <textarea
                  name="message"
                  placeholder="请输入您的留言（500字以内）"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all text-foreground-800 placeholder-foreground-400 resize-none"
                ></textarea>
                <div className="honeypot-wrap">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 mt-2">消息已发送，我们会尽快回复您！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 mt-2">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}